/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { useControlledState } from 'react-stately/useControlledState';
import { type ChangeEvent, useCallback } from 'react';
import {
  Input as AriaInput,
  InputContext as AriaInputContext,
} from 'react-aria-components/Input';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { useContextProps } from 'react-aria-components/slots';
import { ClearButton } from '../button/__internal__/clear';
import { IconProvider } from '../icon/context';
import { InputContext } from './context';
import styles from './styles.module.css';
import type { InputProps } from './types';

// TODO: Improve this implementation so it is more of a realistic event
const clearInputEvent = {
  target: { value: '' },
} as ChangeEvent<HTMLInputElement>;

/**
 * Input - A flexible text input component with enhanced features
 *
 * Provides a customizable text input with automatic sizing, clear functionality,
 * and integrated validation states. Supports various styling options and integrates
 * seamlessly with form field components for comprehensive form experiences.
 *
 * @param props - {@link InputProps}
 * @param props.ref - Ref to the input element.
 * @param props.classNames - CSS class names for input subcomponents.
 * @param props.autoSize - Whether the input should auto-size to its content.
 * @param props.prefix - Content to display before the input.
 * @param props.size - Size variant for the input.
 * @param props.suffix - Content to display after the input.
 * @param props.isClearable - Whether the input shows a clear button.
 * @param props.isInvalid - Whether the input is in an invalid state.
 * @returns The rendered Input component.
 *
 * @example
 * ```tsx
 * // Basic input
 * <Input placeholder="Enter text..." />
 * ```
 *
 * @example
 * ```tsx
 * // Input with clear button
 * <Input
 *   defaultValue="Clearable text"
 *   classNames={{ clear: "hover:bg-info-bold" }}
 * />
 * ```
 */

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: just a little bit over, not worth refactoring
export function Input({ ref = null, ...props }: InputProps) {
  /**
   * It is necessary to pull in the AriaInputContext to capture defaultValue,
   * value & onChange props that may be supplied by a Field component
   *
   * These are necessary due to the implementation of useControlledState for
   * the purposes of supporting the clear button and to capture the length
   * of the current value for the autoSize feature
   */
  [props, ref] = useContextProps(props, ref, AriaInputContext);
  [props, ref] = useContextProps({ ...props }, ref, InputContext);

  const {
    classNames,
    autoSize,
    defaultValue = '',
    disabled,
    placeholder,
    prefix: prefixProp,
    readOnly,
    required,
    size = 'medium',
    suffix: suffixProp,
    type = 'text',
    value: valueProp,
    isClearable = true,
    isInvalid,
    onChange,
    onKeyDown,
    ...rest
  } = props;

  const [value, setValue] = useControlledState(valueProp, defaultValue);
  const length = (`${value ?? ''}`.length || placeholder?.length) ?? 0;
  const prefix = !!prefixProp && styles.hasPrefix;
  const suffix = !!suffixProp && styles.hasSuffix;
  const clear = isClearable && styles.isClearable;
  const isEmpty = value == null || value === '';

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);

      if (!event.defaultPrevented) {
        setValue(event.target.value);
      }
    },
    [onChange, setValue],
  );
  const handleClearButtonPress = useCallback(() => {
    handleChange(clearInputEvent);

    ref?.current?.focus();
  }, [handleChange, ref]);

  return (
    <IconProvider size='small'>
      <div
        className={clsx(
          'group/input',
          styles.container,
          prefix,
          suffix,
          clear,
          classNames?.container,
        )}
        data-disabled={disabled || null}
        data-empty={isEmpty || null}
        data-invalid={isInvalid || null}
        data-length={length}
        data-placeholder={(!!placeholder && isEmpty) || null}
        data-readonly={readOnly || null}
        data-required={required || null}
        data-size={size}
      >
        {prefix && (
          <span className={clsx(styles.prefix, classNames?.prefix)}>
            {prefixProp}
          </span>
        )}
        <div
          className={clsx(
            styles.sizer,
            classNames?.sizer,
            autoSize && styles.autoSize,
          )}
        >
          <AriaInput
            {...rest}
            ref={ref}
            className={composeRenderProps(classNames?.input, (className) =>
              clsx(styles.input, className),
            )}
            title={value ? String(value) : ''}
            disabled={disabled}
            placeholder={placeholder}
            readOnly={readOnly}
            required={required}
            type={type}
            value={value}
            onChange={handleChange}
            onKeyDown={(event) => {
              onKeyDown?.(event);

              if (
                isClearable &&
                !event.defaultPrevented &&
                event.key === 'Escape'
              ) {
                handleChange(clearInputEvent);
              }
            }}
          />
        </div>
        {suffix && (
          <span
            className={clsx(
              styles.suffix,
              prefix,
              suffix,
              clear,
              classNames?.suffix,
            )}
          >
            {suffixProp}
          </span>
        )}
        {!readOnly && isClearable && (
          <ClearButton
            className={composeRenderProps(classNames?.clear, (className) =>
              clsx(styles.clear, prefix, suffix, clear, className),
            )}
            isDisabled={disabled}
            onPress={handleClearButtonPress}
          />
        )}
      </div>
    </IconProvider>
  );
}
